import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { trackAffiliateClick } from "@/utils/tracking";
import { logger } from "../../utils/logger";

/* ── Token values (inlined to avoid babel-metadata-plugin issues) ── */
const GRADIENT = "linear-gradient(135deg, #ef4444, #f59e0b 30%, #a78bfa 70%, #3b82f6)";
const BORDER = "2.5px";
const TRANSITION = "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
const GLOW_LIGHT = {
  default: "0 4px 16px rgba(239,68,68,0.08), 0 4px 16px rgba(59,130,246,0.08), 0 6px 24px rgba(0,0,0,0.06)",
  hover: "0 6px 24px rgba(239,68,68,0.15), 0 6px 24px rgba(59,130,246,0.15), 0 10px 36px rgba(0,0,0,0.08)",
};
const GLOW_DARK = {
  default: "0 4px 16px rgba(239,68,68,0.10), 0 4px 16px rgba(59,130,246,0.10), 0 6px 24px rgba(0,0,0,0.10)",
  hover: "0 6px 24px rgba(239,68,68,0.20), 0 6px 24px rgba(59,130,246,0.20), 0 10px 36px rgba(0,0,0,0.12)",
};
const INNER_LIGHT = "linear-gradient(145deg, rgba(255,255,255,0.97), rgba(245,247,250,0.95))";
const INNER_DARK = "linear-gradient(145deg, rgba(240,242,248,0.95), rgba(230,235,242,0.92))";

const SIZES = {
  sm: { py: "7px", px: "16px", fontSize: "12px", gap: "6px", radius: "12px", iconSize: 12, subtextSize: "9px" },
  md: { py: "10px", px: "22px", fontSize: "13px", gap: "8px", radius: "16px", iconSize: 14, subtextSize: "10px" },
  lg: { py: "13px", px: "28px", fontSize: "14.5px", gap: "9px", radius: "18px", iconSize: 16, subtextSize: "11px" },
};

const VALID_PLACEMENTS = [
  "firm_card", "firm_page_header", "firm_page_pricing", "comparison_table",
  "discount_page_header", "discount_hub_table", "promo_strip_home", "comparison_page",
  "comparison_cta", "best_for_you", "best_for_you_topstep_apex", "best_for_you_bulenox_tradeday", "best_for_you_e8_bg",
  "best_for_you_fast_payouts", "best_for_you_under100", "best_for_you_under200", "best_for_you_no_activation",
  "bulenox_alternatives", "tradeday_alternatives", "topstep_alternatives", "apex_alternatives",
  "best_prop_firms_2026_top", "best_2026_ranking", "homepage_live_deals",
  "category_fastest_payouts", "category_best_value", "category_beginner_friendly", "category_highest_split",
  "firm_top_summary", "review_page_top", "review_page_bottom",
  "quiz_result", "firm_page_sidebar", "firm_page_mobile_sidebar",
];

const SUBTEXT_PATHS = ["/firms/", "-discount", "/futures-prop-firm-comparison", "/futures-prop-firm-discounts"];

function shouldShowSubtext(pathname) {
  if (pathname === "/futures-prop-firm-comparison") return true;
  if (pathname === "/futures-prop-firm-discounts") return true;
  return false;
}

/**
 * AffiliateCTA — Primary outbound CTA for ALL affiliate links.
 * Gradient border, glow, tracked clicks, sponsored rel.
 */
export const AffiliateCTA = ({
  href,
  label,
  firmSlug,
  placement,
  size = "lg",
  showSubtext,
  className = "",
  ...rest
}) => {
  const [disabled, setDisabled] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === "dark";

  if (process.env.NODE_ENV !== "production" && placement && !VALID_PLACEMENTS.includes(placement)) {
    logger.warn("[AffiliateCTA] Invalid placement:", placement);
  }

  const s = SIZES[size] || SIZES.lg;
  const glow = isDark ? GLOW_DARK : GLOW_LIGHT;
  const innerBg = isDark ? INNER_DARK : INNER_LIGHT;

  const autoSubtext = showSubtext !== undefined
    ? showSubtext
    : shouldShowSubtext(location.pathname);

  const handleClick = useCallback((e) => {
    if (disabled) { e.preventDefault(); return; }
    setDisabled(true);
    try { trackAffiliateClick(label, firmSlug || "", href, placement || "unknown"); } catch (err) { /* fire-and-forget */ }
    setTimeout(() => setDisabled(false), 400);
  }, [disabled, label, firmSlug, href, placement]);

  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="sponsored nofollow noopener noreferrer"
      title="Opens official site"
      onClick={handleClick}
      className={`group inline-flex flex-col items-center no-underline ${disabled ? "opacity-70 pointer-events-none" : ""} ${className}`}
      style={{
        padding: BORDER,
        borderRadius: s.radius,
        background: GRADIENT,
        boxShadow: glow.default,
        transition: TRANSITION,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.boxShadow = glow.hover;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = glow.default;
        e.currentTarget.style.transform = "translateY(0)";
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.99)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      {...rest}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: s.gap,
          padding: `${s.py} ${s.px}`,
          borderRadius: `calc(${s.radius} - ${BORDER})`,
          background: innerBg,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          color: "#0f172a",
          fontSize: s.fontSize,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
          width: "100%",
          transition: "background 0.3s ease",
        }}
      >
        {label}
        <span className="relative inline-flex items-center" style={{ width: s.iconSize, height: s.iconSize }}>
          <ArrowRight
            size={s.iconSize}
            strokeWidth={2.2}
            className="absolute inset-0 transition-all duration-200 group-hover:opacity-0 group-hover:translate-x-1"
            style={{ opacity: 0.85 }}
          />
          <ExternalLink
            size={s.iconSize - 2}
            strokeWidth={2.2}
            className="absolute inset-0 transition-all duration-200 opacity-0 -translate-x-1 group-hover:opacity-85 group-hover:translate-x-0"
          />
        </span>
      </span>
      {autoSubtext && (
        <span
          style={{
            fontSize: s.subtextSize,
            color: isDark ? "rgba(148,163,184,0.8)" : "rgba(100,116,139,0.8)",
            marginTop: "4px",
            letterSpacing: "0.02em",
          }}
        >
          Official site
        </span>
      )}
    </a>
  );
};

/* Re-export token values for external use */
export { VALID_PLACEMENTS, GRADIENT, GLOW_LIGHT, GLOW_DARK, INNER_LIGHT, INNER_DARK, BORDER, TRANSITION, SIZES };
